import {
  WEEKDAY_LETTERS,
  formatHoursLabel,
  hourBoxTone,
  type HourBoxTone,
} from '../utils/weekHours'

interface WeekHoursRowProps {
  /** Hours for Mon–Sun (length 7). Missing/undefined treated as 0. */
  hours: number[]
  /** Optional yyyy-mm-dd keys for tooltips. */
  dayKeys?: string[]
}

const TONE_CLASS: Record<HourBoxTone, string> = {
  empty: 'bg-gray-200 text-gray-400 border-gray-300',
  low: 'bg-yellow-200 text-yellow-900 border-yellow-400',
  ok: 'bg-green-200 text-green-900 border-green-500',
  high: 'bg-red-200 text-red-900 border-red-500',
}

export function WeekHoursRow({ hours, dayKeys }: WeekHoursRowProps) {
  const days = Array.from({ length: 7 }, (_, i) => Number(hours[i]) || 0)
  const weekTotal = days.reduce((sum, h) => sum + h, 0)
  const totalLabel = formatHoursLabel(weekTotal)

  return (
    <div
      className="grid grid-cols-8 gap-0.5"
      aria-label="Weekly hours tracked in ProWorkflow"
    >
      {days.map((h, i) => {
        const tone = hourBoxTone(h)
        const label = formatHoursLabel(h)
        const title = dayKeys?.[i]
          ? `${WEEKDAY_LETTERS[i]} ${dayKeys[i]}: ${h > 0 ? `${formatHoursLabel(h)}h` : 'no time'}`
          : `${WEEKDAY_LETTERS[i]}: ${h > 0 ? `${formatHoursLabel(h)}h` : 'no time'}`

        return (
          <div key={i} className="flex flex-col items-center gap-0.5 min-w-0">
            <span className="text-[8px] font-medium text-gray-500 leading-none">
              {WEEKDAY_LETTERS[i]}
            </span>
            <div
              title={title}
              className={`w-full aspect-square rounded border flex items-center justify-center text-[9px] font-semibold tabular-nums ${TONE_CLASS[tone]}`}
            >
              {label}
            </div>
          </div>
        )
      })}

      <div className="flex flex-col items-center gap-0.5 min-w-0">
        <span className="text-[8px] font-medium text-gray-500 leading-none">Σ</span>
        <div
          title={`Week total: ${weekTotal > 0 ? `${formatHoursLabel(weekTotal)}h` : 'no time'}`}
          className="w-full aspect-square rounded border flex items-center justify-center text-[9px] font-bold tabular-nums bg-slate-100 text-slate-800 border-slate-400"
        >
          {totalLabel}
        </div>
      </div>
    </div>
  )
}
